import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import requestIdleCallback, { cancelIdleCallback } from "ric-shim";

/**
 * Use a semi-cached version of a value, that will update but with delay based on requestIdleCallback.
 * Ideal to pass to expensive memos that can work with old data. */
export function useThrottledValue<T>(value: T, timeout: number) {
	const [throttledValue, changeThrottledValue] = useState(value);

	// TODO: Decide whether to blame Chrome, Parcel, or ric-shim for this .call nonsense
	useEffect(() => {
		if (throttledValue !== value) {
			const handle = requestIdleCallback.call(
				null,
				() => {
					changeThrottledValue(value);
				},
				{ timeout }
			);
			return () => cancelIdleCallback.call(null, handle);
		}
	}, [value, throttledValue]);

	return throttledValue;
}

/** Like useState(), but returns a getter function as well.
 * The getter function is stable and can be used to retrieve the current state value at any time */
export function useStateWithGetter<S>(
	initialState: S | (() => S)
): [S, React.Dispatch<React.SetStateAction<S>>, () => S] {
	const [state, setState] = useState(initialState);
	const ref = useRef(state);
	useEffect(() => {
		ref.current = state;
	});
	const getState = useCallback(() => ref.current, []);
	return [state, setState, getState];
}

export function useStableGetter<V>(value: V) {
	const ref = useRef(value);
	useLayoutEffect(() => {
		ref.current = value;
	}, [value]);
	return useCallback(() => ref.current, []);
}

type ArrayWithUndefined<T extends readonly any[]> = { [K in keyof T]: T[K] | undefined };
type Destructor = () => void;

/** useEffect(), but you get passed the previous values.  Type the deps array `as const` for the most useful inferences. */
export function useEffectWithPrev<TDeps extends readonly any[]>(
	callback: (...prev: ArrayWithUndefined<TDeps>) => void | Destructor,
	deps: TDeps
) {
	const valueStore = useRef<readonly any[]>(Array.from({ length: deps.length }));
	useEffect(() => {
		const ret = callback(...(valueStore.current as any));
		valueStore.current = deps;
		return ret;
	}, deps);
}

/**
 * useEffect(), but the first run is skipped.  In general, this should only be used when you are interfacing non-react code
 * that has an "initial" vs "update" method, and you handled the initial in a separate effect.
 */
export const useEffectSkipFirst: typeof useEffect = (effect, deps) => {
	const sawInitial = useRef(false);
	useEffect(() => {
		if (!sawInitial.current) {
			sawInitial.current = true;
			return undefined;
		} else {
			return effect();
		}
	}, deps);
};

/**
 * useLayoutEffect(), but the first run is skipped.  In general, this should only be used when you are interfacing non-react code
 * that has an "initial" vs "update" method, and you handled the initial in a separate effect.
 */
export const useLayoutEffectSkipFirst: typeof useLayoutEffect = (effect, deps) => {
	const sawInitial = useRef(false);
	useLayoutEffect(() => {
		if (!sawInitial.current) {
			sawInitial.current = true;
			return undefined;
		} else {
			return effect();
		}
	}, deps);
};
